// NOTE: Keep these types in sync with the corresponding definitions in assessment/types.py.
//
// TODO: extend the generate_client_sources management command to automatically generate TS types
// from python sources, then create this file by feeding assessment/types.py into it.

import { SectionRole } from "../shared/types/roles";

export enum AssessmentCategory {
  prepwork = "prepwork",
  activities = "activities",
  assignments = "assignments",
}

export type SectionScoreTableRow = {
  student_id: string;
  student_name: string;
  activities_score: string | null;
  assignments_score: string | null;
  prep_work_score: string | null;
  total_section_score: string;
};

// The full section score report is generated from the admin home page. Its header keys
// are a superset of the brief report's header keys, but two reports differ significantly
// in the values they assign to those keys, hence the separate representation.
export enum SectionScoreFullReportHeader {
  activities_score = "activities_score",
  assessed_pages_count = "assessed_pages_count",
  assignments_score = "assignments_score",
  course_code = "course_code",
  email = "email",
  external_id = "external_id",
  prep_score = "prep_score",
  section_external_id = "section_external_id",
  section_score_percent = "section_score_percent",
  section_score_rubric_4 = "section_score_rubric_4",
  section_score_rubric_5 = "section_score_rubric_5",
  section_title = "section_title",
  student_name = "student_name",
  term_title = "term_title",
}

// The brief section score report is generated from the section dashboard. Its header keys
// are a proper subset of the full report's header keys, but two reports differ significantly
// in the values they assign to those keys, hence the separate representation.
export enum SectionScoreBriefReportHeader {
  activities_score = "activities_score",
  assignments_score = "assignments_score",
  prep_score = "prep_score",
  section_score_percent = "section_score_percent",
  section_score_rubric_4 = "section_score_rubric_4",
  section_score_rubric_5 = "section_score_rubric_5",
  student_name = "student_name",
}

export enum AssessmentPublishingStatus {
  published = "published",
  draft = "draft",
}

export type HomeAssessmentTableRole =
  | SectionRole.STUDENT
  | SectionRole.INSTRUCTOR
  | SectionRole.SECTION_ASSISTANT;

export type AllAssessmentTableRow = {
  page_id: string;
  title: string;
  topic_title: string | null;
  status: string;
  unassessed_late_submissions_count: number;
  unseen_late_submissions_count: number;
  unseen_submissions_count: number;
  assessed_submissions_count: number;
  total_submissions_count: number;
  is_marked_complete: boolean;
  is_assessment_complete: boolean;
  publishing: AssessmentPublishingStatus;
  assessed_count: number;
  assessment_category: AssessmentCategory;
  avg_points: string;
  outcomes_count: number;
  outcomes_tooltip: string;
  last_submitted_date: string | null;
  hidden: boolean;
  due_at: string | null;
  submissions_last_collected_at: string | null;
  progress: string;
};

export type HomeAssessmentTableRow = {
  page_id: string;
  title: string;
  topic_title: string | null;
  status: string;
  unassessed_late_submissions_count: number;
  unseen_late_submissions_count: number;
  unseen_submissions_count: number;
  assessed_submissions_count: number;
  total_submissions_count: number;
  is_marked_complete: boolean;
  is_assessment_complete: boolean;
  publishing: AssessmentPublishingStatus;
  assessment_category: AssessmentCategory;
  avg_points: string;
  outcomes_count: number;
  outcomes_tooltip: string;
  hidden: boolean;
  due_at: string | null;
  submissions_last_collected_at: string | null;
  section_id: string;
  section_title: string;
  code_title: string;
  assessing_marked_complete_at: string | null;
  last_submitted_at: string | null;
};

export type StudentHomeAssessmentTableRow = {
  page_id: string;
  title: string;
  topic_title: string | null;
  section_id: string;
  section_title: string;
  course_code: string;
  category: string;
  activity_category: string | null;
  assessment_category: AssessmentCategory;
  outcomes_count: number;
  max_points: number | string | null;
  points: number | string | null;
  comment: string;
  submitted_at: string;
  hidden: boolean;
};

export enum AllAssessmentReportHeader {
  term_title = "term_title",
  course_code = "course_code",
  section_title = "section_title",
  page_topic = "page_topic",
  page = "page",
  status = "status",
  publishing = "publishing",
  submissions = "submissions",
  progress = "progress",
  assessment_category = "assessment_category",
  avg_points = "avg_points",
  outcomes = "outcomes",
  last_submitted = "last_submitted",
}

export type StudentAssessmentScoreComment = {
  assessment_id: string;
  comment: string;
};

export type StudentAssessmentScoreTableRow = {
  page_id: string;
  title: string;
  topic_title: string | null;
  assessment_category: AssessmentCategory;
  points: string;
  outcomes_count: number;
  outcomes_tooltip: string;
  submitted_at: string | null;
  assessment_comments: StudentAssessmentScoreComment[];
  hidden: boolean;
};

/**
 * --------------------------------------------------------------------
 * Outcome Scores
 * --------------------------------------------------------------------
 */

export type OutcomeScoreTableHeader = {
  id: string;
  name: string;
};

export type OutcomeScoreTableRow = {
  student_id: string;
  student_name: string;
  outcome_scores: (number | null)[];
};

export type OutcomeScoreTableData = {
  headers: OutcomeScoreTableHeader[];
  rows: OutcomeScoreTableRow[];
};

export enum OutcomeScoreReportHeader {
  student_name = "student_name",
}

/**
 * --------------------------------------------------------------------
 * Page Points
 * --------------------------------------------------------------------
 */

export type PagePointTableHeader = {
  id: string;
  name: string;
};

export type PagePointTableRow = {
  // The "Student Points Per Page" table has a variable number of columns, including
  // three fixed columns followed by one column per student. The `student_scores` field
  // below contains the data for those variable columns.
  page_id: string;
  page_title: string;
  page_released_at: string | null;
  assessment_category: string;
  avg_points: string;
  student_scores: string[];
};

export type PagePointTableData = {
  headers: PagePointTableHeader[];
  rows: PagePointTableRow[];
};

export enum PagePointReportHeader {
  assessment_category = "assessment_category",
  avg_points = "avg_points",
  page = "page",
}

export type SectionOutcomeColumn = {
  section_outcome_id: string;
  title: string;
};

export type StudentOutcomeScoreEntry = {
  section_outcome_id: string;
  score: number | null;
};

export type StudentOutcomeScoreTableRow = {
  page_id: string;
  title: string;
  assessment_category: AssessmentCategory;
  hidden: boolean;
  outcome_scores: StudentOutcomeScoreEntry[];
};

export type StudentOutcomeScoreTableData = {
  section_outcomes: SectionOutcomeColumn[];
  pages: StudentOutcomeScoreTableRow[];
};

export type OutcomeAssessmentTableRow = {
  page_id: string;
  section_id: string;
  section_title: string;
  title: string;
  max_points: number | null;
  assessment_created_at: string | null;
  assessment_comment: string | null;
  outcome_score: number | null;
};

export type StudentSectionOutcomeTableData = {
  pages: OutcomeAssessmentTableRow[];
};

export type SectionOutcomeIndexPage = {
  page_id: string;
  title: string;
  max_points: number | null;
};

export type SectionOutcomeIndexTopic = {
  id: string;
  title: string;
  pages: SectionOutcomeIndexPage[];
};

export type SectionOutcomeIndexData = {
  assignments: SectionOutcomeIndexPage[];
  course_resources: SectionOutcomeIndexPage[];
  topics: SectionOutcomeIndexTopic[];
};
